import React from "react";
import Skeleton from "../common/skeleton";

/**
 * WalletStatsCards -- 지갑 상태 통계 요약 카드 컴포넌트
 *
 * Props:
 * - stats (Object|null): {total_coins, normal_count, suspended_count, normal_ratio, recent_changes_24h}
 * - loading (boolean): 로딩 중 여부
 * - error (boolean): 에러 여부
 * - onCardClick (function|null): 클릭 가능한 카드 클릭 시 호출 (filterKey 인자)
 * - activeFilterKey (string|null): 현재 활성 필터 키 ("suspended" 등) -- 하이라이트 대상
 */
function WalletStatsCards({
    stats = null,
    loading = false,
    error = false,
    onCardClick = null,
    activeFilterKey = null,
}) {
    /* -- 로딩 상태 -- */
    if (loading) {
        return (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} variant="card" />
                ))}
            </div>
        );
    }

    /* -- 에러 상태 -- */
    if (error) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                <div className="rounded-lg border border-loss/20 bg-loss/5 p-4 text-center text-sm text-loss">
                    데이터를 불러올 수 없습니다.
                </div>
            </div>
        );
    }

    const cards = [
        {
            label: "전체 코인",
            value: stats ? stats.total_coins : "--",
            colorClass: "text-content",
            filterKey: null,
        },
        {
            label: "정상 비율",
            value: stats ? `${stats.normal_ratio}%` : "--",
            colorClass: "text-gain",
            filterKey: null,
        },
        {
            label: "정지 코인",
            value: stats ? stats.suspended_count : "--",
            colorClass: "text-loss",
            filterKey: "suspended",
        },
        {
            label: "24시간 변경",
            value: stats ? stats.recent_changes_24h : "--",
            colorClass: "text-warning",
            filterKey: null,
        },
    ];

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {cards.map((card) => {
                const clickable = card.filterKey && onCardClick;
                const active = clickable && card.filterKey === activeFilterKey;
                const baseClass = "rounded-xl border bg-surface-card p-4 shadow-card transition-colors";
                const borderClass = active ? "border-loss" : "border-surface-border";
                const clickClass = clickable
                    ? "cursor-pointer hover:bg-surface-hover hover:border-loss/50"
                    : "";
                return (
                    <div
                        key={card.label}
                        className={`${baseClass} ${borderClass} ${clickClass}`}
                        onClick={clickable ? () => onCardClick(card.filterKey) : undefined}
                        role={clickable ? "button" : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        aria-label={clickable ? `${card.label} 필터 ${active ? "해제" : "활성화"}` : undefined}
                        onKeyDown={clickable ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onCardClick(card.filterKey);
                            }
                        } : undefined}
                    >
                        <div className="text-xs text-content-muted">{card.label}</div>
                        <div className={`mt-1 text-2xl font-bold ${card.colorClass}`}>
                            {card.value}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default WalletStatsCards;
